import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Twitter, Linkedin, MessageCircle, Globe, 
  BookOpen, Heart, Cpu, Users, Coins
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_94b15709-33c0-4b3c-a307-474349afd7f8/artifacts/vb9rqttx_ChatGPT%20Image%20Feb%201%2C%202026%20at%2008_39_11%20AM.png";

const Footer = () => {
  const pillars = [
    { icon: BookOpen, label: 'Education', color: '#3B82F6' },
    { icon: Heart, label: 'Wellness', color: '#4ADE80' },
    { icon: Cpu, label: 'Technology', color: '#8B5CF6' },
    { icon: Users, label: 'Community', color: '#FB923C' },
    { icon: Coins, label: 'Finance', color: '#D4AF37' },
  ];

  return (
    <footer className="relative mt-20" data-testid="footer">
      {/* Top border gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="AIH Universe" className="h-12 w-12 rounded-lg gold-glow" />
              <div>
                <h3 className="font-unbounded font-bold text-xl text-white">AIH Universe</h3>
                <p className="text-xs text-[#D4AF37]">All In Hand</p>
              </div>
            </Link>
            <p className="text-sm text-white/60 mb-6">
              A unified portal for work, learning, earning, creating & investing. 
              The super-app of the AIH digital nation.
            </p>
            <div className="flex gap-3">
              <a 
                href="https://x.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors"
                data-testid="social-twitter"
              >
                <Twitter size={18} className="text-white/60" />
              </a>
              <a 
                href="https://t.me" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors"
                data-testid="social-telegram"
              >
                <MessageCircle size={18} className="text-white/60" />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin size={18} className="text-white/60" />
              </a>
              <a 
                href="https://allinhandtokens.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors"
                data-testid="social-website"
              >
                <Globe size={18} className="text-white/60" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-unbounded font-bold text-sm text-white mb-4 uppercase tracking-wider">
              Platform
            </h4>
            <ul className="space-y-3">
              <li><Link to="/wallet" className="text-white/60 hover:text-[#D4AF37] transition-colors">Wallet</Link></li>
              <li><Link to="/academy" className="text-white/60 hover:text-[#D4AF37] transition-colors">Learning Academy</Link></li>
              <li><Link to="/community" className="text-white/60 hover:text-[#D4AF37] transition-colors">Community Hub</Link></li>
              <li><Link to="/calculator" className="text-white/60 hover:text-[#D4AF37] transition-colors">Staking Calculator</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-unbounded font-bold text-sm text-white mb-4 uppercase tracking-wider">
              Resources
            </h4>
            <ul className="space-y-3">
              <li><a href="https://allinhandtokens.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#D4AF37] transition-colors">Token Presale</a></li>
              <li><a href="https://allinhandstaking.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#D4AF37] transition-colors">Staking Portal</a></li>
              <li><a href="#" className="text-white/60 hover:text-[#D4AF37] transition-colors">Whitepaper</a></li>
              <li><a href="#" className="text-white/60 hover:text-[#D4AF37] transition-colors">Documentation</a></li>
            </ul>
          </div>

          {/* Pillars */}
          <div>
            <h4 className="font-unbounded font-bold text-sm text-white mb-4 uppercase tracking-wider">
              5 Pillars
            </h4>
            <div className="space-y-3">
              {pillars.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <div key={pillar.label} className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${pillar.color}20` }}
                    >
                      <Icon size={16} style={{ color: pillar.color }} />
                    </div>
                    <span className="text-white/60 text-sm">{pillar.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/40">
            &copy; {new Date().getFullYear()} All In Hand Universe. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-white/40">
            <a href="#" className="hover:text-white/60 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white/60 transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-white/60 transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
